import { Dialog } from '@headlessui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import ErrorDisplay from './ErrorDisplay';
import { XIcon } from '@heroicons/react/outline';

export default function UserFormModalRBAC({ isOpen, onClose, onSubmit, initialValues = null }) {
  const isEditing = !!initialValues;
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [governingBodies, setGoverningBodies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  const { isAdmin } = usePermissions();
  const { user: currentUser } = useAuth();
  
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 UserFormModal - Current User:', currentUser);
      console.log('🔍 UserFormModal - Current User Role:', currentUser?.role);
      
      // Fetch available roles from RBAC system (already filtered by backend)
      api.get('/api/roles/available', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(response => {
          console.log('📋 Available roles from API:', response.data);
          const availableRoles = response.data;
          console.log('✅ Backend-filtered roles:', availableRoles);
          
          setRoles(availableRoles);
          
          // If editing, find and set the selected role after roles are loaded
          if (initialValues?.roleId) {
            const role = availableRoles.find(r => r.id === initialValues.roleId);
            setSelectedRole(role);
          }
        })
        .catch(error => {
          console.error('❌ Error fetching roles:', error);
          console.error('Error details:', error.response?.data);
        });
      
      // Fetch companies for selection (only if needed)
      if (currentUser?.role !== 'branch') {
        api.get('/api/companies', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
          .then(response => {
            let filteredCompanies = response.data;
            
            // Filter companies based on current user's role
            if (currentUser.role === 'company') {
              // Company users can only see their own company
              filteredCompanies = response.data.filter(c => c.id === currentUser.companyId);
            }
            
            setCompanies(filteredCompanies);
          })
          .catch(error => console.error('Error fetching companies:', error));
      }
      
      // Fetch governing bodies for selection (only for admin and governing_body users)
      if (currentUser?.role === 'admin' || currentUser?.role === 'governing_body') {
        api.get('/api/governing-bodies', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
          .then(response => setGoverningBodies(response.data))
          .catch(error => console.error('Error fetching governing bodies:', error));
      }
      
      // If editing and there's a companyId, load that company's branches
      if (initialValues?.companyId) {
        setSelectedCompanyId(initialValues.companyId);
        fetchBranches(initialValues.companyId);
      }
      // If adding new user and current user is a company user, fetch branches for their company
      else if (!initialValues && currentUser?.role === 'company' && currentUser?.companyId) {
        setSelectedCompanyId(currentUser.companyId);
        fetchBranches(currentUser.companyId);
      }
    }
  }, [isOpen, initialValues, currentUser]);

  // When company selection changes, load its branches
  const fetchBranches = (companyId) => {
    if (companyId) {
      api.get(`/api/branches?companyId=${companyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(response => setBranches(response.data))
        .catch(error => console.error('Error fetching branches:', error));
    } else {
      setBranches([]);
    }
  };

  const validationSchema = Yup.object({
    username: Yup.string().required(t('forms.required')),
    email: Yup.string().email(t('forms.invalidEmail')).required(t('forms.required')),
    password: isEditing
      ? Yup.string().min(6, t('forms.passwordRequirement'))
      : Yup.string().min(6, t('forms.passwordRequirement')).required(t('forms.required')),
    roleId: Yup.number().required('Role is required'),
    governingBodyId: Yup.number().when('roleId', {
      is: (val) => {
        const role = roles.find(r => r.id === val);
        return role?.name === 'governing_body';
      },
      then: () => Yup.number().required(t('forms.required'))
    }),
    companyId: Yup.number().when('roleId', {
      is: (val) => {
        const role = roles.find(r => r.id === val);
        return role?.name === 'company' || role?.name === 'branch';
      },
      then: () => Yup.number().required(t('forms.required'))
    }),
    branchId: Yup.number().when('roleId', {
      is: (val) => {
        const role = roles.find(r => r.id === val);
        return role?.name === 'branch';
      },
      then: () => Yup.number().required(t('forms.required'))
    }),
    isActive: Yup.boolean()
  });

  const initialFormValues = initialValues ? {
    ...initialValues,
    password: '', // Don't pre-fill password
    sendWelcomeEmail: false // Don't send on edit
  } : {
    username: '',
    email: '',
    password: '',
    roleId: '',
    governingBodyId: '',
    // Pre-fill companyId for company users
    companyId: currentUser.role === 'company' ? currentUser.companyId : '',
    branchId: '',
    isActive: true,
    sendWelcomeEmail: true // Default checked for new users
  };

  const handleCompanyChange = (e, setFieldValue) => {
    const companyId = e.target.value;
    setFieldValue('companyId', companyId);
    setFieldValue('branchId', '');
    setSelectedCompanyId(companyId);
    fetchBranches(companyId);
  };

  const handleRoleChange = (e, setFieldValue) => {
    const roleId = parseInt(e.target.value);
    const role = roles.find(r => r.id === roleId);
    setSelectedRole(role);
    setFieldValue('roleId', roleId);
    
    // Clear dependent fields when role changes
    if (role?.name !== 'governing_body') {
      setFieldValue('governingBodyId', '');
    }
    if (role?.name !== 'company' && role?.name !== 'branch') {
      setFieldValue('companyId', '');
      setFieldValue('branchId', '');
    }
    if (role?.name !== 'branch') {
      setFieldValue('branchId', '');
    }
  };

  // Clear error when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setError(null);
    setSelectedRole(null);
    onClose();
  };

  // Handle form submission with proper data formatting
  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Create a copy of values to modify for submission
      const submitData = { ...values };
      const role = roles.find(r => r.id === values.roleId);
      
      // If it's not a branch user, remove branchId field entirely
      if (role?.name !== 'branch') {
        delete submitData.branchId;
      }
      
      // If it's not a company/branch user, remove companyId field
      if (role?.name === 'admin' || role?.name === 'governing_body') {
        delete submitData.companyId;
      }
      
      // If it's not a governing body user, remove governingBodyId field
      if (role?.name !== 'governing_body') {
        delete submitData.governingBodyId;
      }

      // Remove empty password field if editing
      if (isEditing && !submitData.password) {
        delete submitData.password;
      }
      
      // Call the parent component's onSubmit with properly formatted data
      await onSubmit(submitData);
      setError(null);
      handleClose();
    } catch (submitError) {
      console.error('Form submission error:', submitError);
      setError(submitError);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-lg w-full rounded-lg bg-white p-6 my-8">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">
              {isEditing ? t('users.editUser') : t('users.addUser')}
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <ErrorDisplay 
              error={error} 
              onDismiss={() => setError(null)} 
              className="mb-6"
            />
          )}
          
          <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, touched, values, setFieldValue }) => {
              const currentRole = roles.find(r => r.id === values.roleId);
              
              return (
                <Form className="space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                      {t('users.username')} *
                    </label>
                    <Field
                      id="username"
                      name="username"
                      className={`mt-1 input ${errors.username && touched.username ? 'border-red-500' : ''}`}
                      placeholder="Enter username"
                    />
                    {errors.username && touched.username && (
                      <p className="mt-1 text-xs text-red-500">{errors.username}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      {t('users.email')} *
                    </label>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      className={`mt-1 input ${errors.email && touched.email ? 'border-red-500' : ''}`}
                      placeholder="user@example.com"
                    />
                    {errors.email && touched.email && (
                      <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      {t('users.password')} {!isEditing && '*'}
                    </label>
                    <Field
                      id="password"
                      name="password"
                      type="password"
                      className={`mt-1 input ${errors.password && touched.password ? 'border-red-500' : ''}`}
                      placeholder={isEditing ? 'Leave blank to keep current' : 'Enter password'}
                    />
                    {errors.password && touched.password && (
                      <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="roleId" className="block text-sm font-medium text-gray-700">
                      Role *
                    </label>
                    <Field
                      as="select"
                      id="roleId"
                      name="roleId"
                      onChange={(e) => handleRoleChange(e, setFieldValue)}
                      className={`mt-1 input ${errors.roleId && touched.roleId ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select a role</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.displayName}
                          {role.isSystem && ' (System)'}
                        </option>
                      ))}
                    </Field>
                    {errors.roleId && touched.roleId && (
                      <p className="mt-1 text-xs text-red-500">{errors.roleId}</p>
                    )}
                    {currentRole && currentRole.description && (
                      <p className="mt-1 text-xs text-gray-500">{currentRole.description}</p>
                    )}
                  </div>

                  {currentRole?.name === 'governing_body' && (
                    <div>
                      <label htmlFor="governingBodyId" className="block text-sm font-medium text-gray-700">
                        {t('users.governingBody')} *
                      </label>
                      <Field
                        as="select"
                        id="governingBodyId"
                        name="governingBodyId"
                        className={`mt-1 input ${errors.governingBodyId && touched.governingBodyId ? 'border-red-500' : ''}`}
                      >
                        <option value="">{t('forms.selectGoverningBody')}</option>
                        {governingBodies.map(governingBody => (
                          <option key={governingBody.id} value={governingBody.id}>
                            {governingBody.name}
                          </option>
                        ))}
                      </Field>
                      {errors.governingBodyId && touched.governingBodyId && (
                        <p className="mt-1 text-xs text-red-500">{errors.governingBodyId}</p>
                      )}
                    </div>
                  )}

                  {(currentRole?.name === 'company' || currentRole?.name === 'branch') && (
                    <div>
                      <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                        {t('users.company')} *
                      </label>
                      <Field
                        as="select"
                        id="companyId"
                        name="companyId"
                        onChange={(e) => handleCompanyChange(e, setFieldValue)}
                        disabled={currentUser.role === 'company'}
                        className={`mt-1 input ${errors.companyId && touched.companyId ? 'border-red-500' : ''} ${currentUser.role === 'company' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <option value="">{t('forms.selectCompany')}</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </Field>
                      {errors.companyId && touched.companyId && (
                        <p className="mt-1 text-xs text-red-500">{errors.companyId}</p>
                      )}
                    </div>
                  )}

                  {currentRole?.name === 'branch' && (
                    <div>
                      <label htmlFor="branchId" className="block text-sm font-medium text-gray-700">
                        {t('users.branch')} *
                      </label>
                      <Field
                        as="select"
                        id="branchId"
                        name="branchId"
                        className={`mt-1 input ${errors.branchId && touched.branchId ? 'border-red-500' : ''}`}
                        disabled={!selectedCompanyId}
                      >
                        <option value="">{t('forms.selectBranch')}</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </Field>
                      {errors.branchId && touched.branchId && (
                        <p className="mt-1 text-xs text-red-500">{errors.branchId}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center">
                    <Field
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      {t('users.active')}
                    </label>
                  </div>

                  {!isEditing && (
                    <div className="flex items-center bg-blue-50 p-3 rounded-lg">
                      <Field
                        id="sendWelcomeEmail"
                        name="sendWelcomeEmail"
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="sendWelcomeEmail" className="ml-2 block text-sm text-gray-900">
                        {t('email.sendWelcomeEmail')}
                      </label>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      disabled={isSubmitting}
                    >
                      {t('forms.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('forms.saving') : (isEditing ? t('forms.update') : t('forms.create'))}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

